import { useState, useEffect } from "react";
import { useStudio } from "../studios/StudioContext";
import { StripeCardForm } from "./StripeCardForm";
import { PassPurchaseModal } from "./PassPurchaseModal";
import { formatTime, toLocalDateStr, spaceLabel } from "./calendarUtils";
import { useSession, useLoginForm } from "../auth/session";

export function EventModal({ event, date, onClose, prefill = null }) {
  const studio  = useStudio();
  const API     = studio.apiBaseUrl;
  const venues  = studio?.venues ?? [];
  const venue   = venues.find(v => v.id === event.venue);
  const isOnline  = event.venue === "venue_online";
  const isHybrid  = event.isHybrid === true;
  const isSlidingScale = event.priceMin != null && event.priceMax != null;
  const humanDate = date.toLocaleDateString("en-US", {
    weekday:"long", month:"long", day:"numeric", year:"numeric"
  });
  const dateStr = date.toISOString().split('T')[0];
  const [payMode, setPayMode]                     = useState("dropin");
  const [name, setName]                           = useState(prefill?.name || "");
  const [email, setEmail]                         = useState(prefill?.email || "");
  const [passCode, setPassCode]                   = useState("");
  const [phone, setPhone]                         = useState("");
  const [codeStatus, setCodeStatus]               = useState(null);
  const [passData, setPassData]                   = useState(null);
  const [checking, setChecking]                   = useState(false);
  const [booking, setBooking]                     = useState(false);
  const [bookError, setBookError]                 = useState(null);
  const [booked, setBooked]                       = useState(false);
  const [waiverSigned, setWaiverSigned]           = useState(false);
  const [passHint, setPassHint]                   = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [couponCode, setCouponCode]               = useState("");
  const [couponDiscount, setCouponDiscount]       = useState(null); // { type, amount, displayText, code }
  const [couponError, setCouponError]             = useState(null);
  const [stripeReady, setStripeReady]             = useState(false);
  const session   = useSession(API);
  const staffUser = session?.user;
  const isStaff   = Boolean(session?.token) && ["teacher", "owner"].includes(staffUser?.role)
                    && !/therap/i.test(event.title || "");
  const [teacherMode, setTeacherMode] = useState(isStaff);
  const [teacherFull, setTeacherFull] = useState(false);
  const [showTeacherLogin, setShowTeacherLogin] = useState(false);
  const tl = useLoginForm(API, (user) => {
    setShowTeacherLogin(false);
    if (["teacher", "owner"].includes(user?.role)) { setTeacherMode(true); setBookError(null); }
    else tl.setError("This account isn't a teacher account.");
  });


  useEffect(() => {
    if (!isStaff) return;
    setTeacherMode(true);
    fetch(`${API}/bookings/capacity?classId=${encodeURIComponent(event.id)}&date=${encodeURIComponent(dateStr)}`)
      .then(r => r.json())
      .then(d => setTeacherFull(Boolean(d.isFull)))
      .catch(() => {});
  }, [API, isStaff, event.id, dateStr]);
  const [chosenAmount, setChosenAmount] = useState(() =>
    event.priceMin != null ? Math.round((event.priceMin + event.priceMax) / 2) : null
  );
  
  useEffect(() => {
  const t = setTimeout(() => setStripeReady(true), 50);
  return () => clearTimeout(t);
   }, []);
  
  const nameValid  = name.trim().length > 0;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canBook    = nameValid && emailValid && (payMode === "dropin" || codeStatus === "valid");

  const handleVerifyCode = async (codeOverride) => {
    const code = codeOverride ?? passCode;
    if (!code.trim()) return;
    setChecking(true);
    setCodeStatus(null);
    setPassData(null);
    try {
      const res  = await fetch(`${API}/passes/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: code.trim() }),
      });
      const data = await res.json();
      if (data.valid) { setCodeStatus("valid"); setPassData(data.pass); }
      else { setCodeStatus("invalid"); }
    } catch { setCodeStatus("invalid"); }
    finally { setChecking(false); }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponError(null);
    try {
      const res  = await fetch(`${API}/coupons/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: couponCode.trim(), passType: "dropin", email: (email || "").trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.valid) { setCouponError(data.error ?? "Invalid code."); return; }
      setCouponDiscount(data.discount);
    } catch { setCouponError("Something went wrong."); }
  };

  const [capStatus, setCapStatus] = useState(null); // null | 'full' | 'waitlist'
  const [capInfo, setCapInfo]     = useState(null); // { left, full, waitlist } for capped in-person classes

  // Spaces left, shown as soon as the pop-up opens. With a waitlist link, the student's
  // own held spot doesn't count against them.
  useEffect(() => {
    if (!event.cap || isOnline) return;
    const who = prefill?.email ? `&email=${encodeURIComponent(prefill.email)}` : "";
    fetch(`${API}/bookings/capacity?classId=${encodeURIComponent(event.id)}&date=${encodeURIComponent(dateStr)}${who}`)
      .then(r => r.json())
      .then(d => {
        if (d.spotsLeft == null) return;
        setCapInfo({ left: d.spotsLeft, full: d.isFull, waitlist: !!d.waitlistEnabled });
        setCapStatus(d.isFull ? (d.waitlistEnabled ? 'waitlist' : 'full') : null);
      })
      .catch(() => {});
  }, [API, event.id, event.cap, isOnline, dateStr, prefill?.email]);

const handleEmailBlur = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    try {
      const res  = await fetch(`${API}/waiver/status?email=${encodeURIComponent(trimmedEmail)}`);
      const data = await res.json();
      setWaiverSigned(data.signed ?? data.waiverSigned ?? false);
    } catch { /* silent */ }
    try {
      const res  = await fetch(`${API}/passes/active?email=${encodeURIComponent(trimmedEmail)}`);
      const data = await res.json();
      if (data.pass && !data.pass.code.startsWith("PRIV-")) {
        setPassHint(data.pass);
        setPassCode(data.pass.code);
        await handleVerifyCode(data.pass.code);
      }
    } catch { /* silent */ }
    try {
      const res = await fetch(`${API}/bookings/capacity?classId=${encodeURIComponent(event.id)}&date=${encodeURIComponent(dateStr)}&email=${encodeURIComponent(trimmedEmail)}`);
      const data = await res.json();
      if (data.spotsLeft != null) setCapInfo({ left: data.spotsLeft, full: data.isFull, waitlist: !!data.waitlistEnabled });
      if (data.isFull) {
        setCapStatus(data.waitlistEnabled ? 'waitlist' : 'full');
      } else {
        setCapStatus(null);
      }
    } catch { /* silent */ }
    try {
      const res = await fetch(`${API}/students/${encodeURIComponent(trimmedEmail)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.student?.phone) setPhone(data.student.phone);
      }
    } catch { /* silent */ }
  };

  // Arrived from a waitlist "Confirm my spot" link: look up their pass, waiver and held spot
  useEffect(() => {
    if (prefill?.email) handleEmailBlur();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDropinSuccess = async (paymentIntentId) => {
    const bookRes  = await fetch(`${API}/bookings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: name.trim(), email: email.trim(), phone: phone.trim(),
        classId: event.id, date: toLocalDateStr(date),
        paymentType: "dropin", stripePaymentId: paymentIntentId,
        couponCode: couponDiscount ? couponDiscount.code : undefined,
      }),
    });

    const bookData = await bookRes.json();
    if (!bookRes.ok) { setBookError(bookData.message ?? bookData.error ?? "Booking failed. Please try again."); return; }
    setBooked(true);
  };
  
   const handleJoinWaitlist = async () => {
       try {
         const res = await fetch(`${API}/bookings`, {
           method: "POST",
           headers: { "Content-Type": "application/json" },
           body: JSON.stringify({
             name: name.trim(), email: email.trim(), phone: phone.trim(),
             classId: event.id, date: toLocalDateStr(date),
             paymentType: "waitlist",
           }),
         });
         const data = await res.json();
         if (data.waitlisted) {
           setBooked({ waitlisted: true, position: data.position });
         } else if (data.spotAvailable) {
           setCapStatus(null);
           setBookError("A spot just opened — you can book it now.");
         } else {
           setBookError(data.error || "Could not join waitlist.");
         }
       } catch {
         setBookError("Could not join waitlist.");
       }
     };

  const handleTeacherBook = async () => {
    if (booking) return;
    setBooking(true);
    setBookError(null);
    try {
      const res  = await fetch(`${API}/bookings/teacher`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.token}` },
        body: JSON.stringify({ classId: event.id, date: toLocalDateStr(date) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setBookError("Your teacher sign-in has expired. Sign in to the admin again, then book."); return; }
      if (!res.ok) { setBookError(data.error ?? "Booking failed. Please try again."); return; }
      setBooked(true);
    } catch { setBookError("Something went wrong. Please try again."); }
    finally { setBooking(false); }
  };

  const handlePassBook = async () => {
    if (!canBook || booking) return;
    setBooking(true);
    setBookError(null);
    try {
      const bookRes  = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(), email: email.trim(), phone: phone.trim(),
          classId: event.id, date: toLocalDateStr(date),
          paymentType: passData?.type ?? "pass4",
          passId: passData?.id ?? null,
        }),
      });
      const bookData = await bookRes.json();
      if (!bookRes.ok) { setBookError(bookData.message ?? "Booking failed. Please try again."); return; }
      setBooked(true);
      const refreshRes  = await fetch(`${API}/passes/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: passCode.trim() }),
      });
      const refreshData = await refreshRes.json();
      if (refreshData.valid && refreshData.pass) setPassData(refreshData.pass);
    } catch { setBookError("Something went wrong. Please try again."); }
    finally { setBooking(false); }
  };

  if (booked) {
    if (booked.waitlisted) {
      return (
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={onClose}>✕</button>
            <div className="booking-success">
              <div className="booking-success-icon">⏳</div>
              <h2 className="booking-success-title">You're on the waitlist!</h2>
              <p className="booking-success-sub">
                You're <strong>#{booked.position}</strong> on the waitlist for this class.
              </p>
              <p className="booking-success-sub" style={{marginTop:"0.5rem",fontSize:"0.85rem",opacity:0.75}}>
                If a spot opens while you're first in line, we'll email {email} a link to book it. You haven't been charged.
              </p>
              <div className={`booking-class-summary ${isOnline ? "summary-online" : "summary-studio"}`} style={{marginTop:"1.25rem"}}>
                <span className="summary-title">{event.title}</span>
                <span className="summary-meta">{humanDate} · {formatTime(event.time)}</span>
              </div>
              <button className="book-btn" style={{marginTop:"1.25rem"}} onClick={onClose}>Done</button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>✕</button>
          <div className="booking-success">
            <div className="booking-success-icon">✓</div>
            <h2 className="booking-success-title">You're booked!</h2>
            <p className="booking-success-sub">
              {isHybrid
               ? `See you at ${venue?.name} — a Zoom link will also be sent to your email.`
               : isOnline ? "A Zoom link will be sent to your email before class." : `See you at ${venue?.name}.`}
            </p>
            {passData && (
              <p className="booking-success-sub" style={{marginTop:"0.5rem",fontSize:"0.85rem",opacity:0.75}}>
                {passData.classesRemaining > 0
                  ? `${passData.classesRemaining} class${passData.classesRemaining !== 1 ? "es" : ""} remaining on your pass.`
                  : "That was your last class on this pass."}
              </p>
            )}
            <div className={`booking-class-summary ${isOnline ? "summary-online" : "summary-studio"}`} style={{marginTop:"1.25rem"}}>
              <span className="summary-title">{event.title}</span>
              <span className="summary-meta">{humanDate} · {formatTime(event.time)}</span>
            </div>
            <button className="book-btn" style={{marginTop:"1.25rem"}} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-badges">
          <div className={`modal-badge ${isHybrid ? "badge-hybrid" : isOnline ? "badge-online" : "badge-studio"}`}>
          {isHybrid ? "In Studio + Online" : isOnline ? "Online" : "In Studio"}
          </div>
          {event.subject && <div className="modal-badge badge-subject" data-subj={event.subject}>{event.subject}</div>}
        </div>
        <h2 className="modal-title">{event.title}</h2>
        <div className="modal-meta" style={{marginBottom:"1.25rem"}}>
          <div className="meta-row"><span className="meta-icon">📅</span><span>{humanDate}</span></div>
          <div className="meta-row"><span className="meta-icon">🕐</span><span>{formatTime(event.time)} · {event.duration} min</span></div>
          <div className="meta-row"><span className="meta-icon">👤</span><span>{event.teacher}</span></div>
          {capInfo && (
            <div className="meta-row"><span className="meta-icon">🪑</span>
              <span className={capInfo.full ? "em-space em-space--full" : "em-space"}>{spaceLabel(capInfo)}</span>
            </div>
          )}
          <div className="meta-row"><span className="meta-icon">📍</span>
          <span>
            {isHybrid
              ? <>{venue?.address}<br /><span style={{color:"var(--color-grey-400)",fontSize:"0.875em"}}>+ Online — Zoom link sent on booking</span></>
              : isOnline ? "Online — link sent on booking" : venue?.address}
          </span>
        </div>
        </div>

        {teacherMode ? (
          <div className="pass-block">
            <p className="pass-hint">
              Signed in as <strong>{staffUser?.name || staffUser?.email}</strong> · teacher — classes are free for you.
            </p>
            {teacherFull && <div className="em-cap-alert em-cap-alert--full">This class is full.</div>}
            {bookError && <p className="pass-feedback invalid">{bookError}</p>}
            <button className="book-btn" disabled={booking || teacherFull} onClick={handleTeacherBook}>
              {booking ? "Booking…" : "Book free"}
            </button>
            <p className="stripe-hint">
              <button className="stripe-pass-link" onClick={() => { setTeacherMode(false); setBookError(null); }}>
                Book as a student instead →
              </button>
            </p>
          </div>
        ) : (
        <>
          {isStaff ? (
          <p className="stripe-hint">
            <button className="stripe-pass-link" onClick={() => { setTeacherMode(true); setBookError(null); }}>
              Book free as a teacher →
            </button>
          </p>
        ) : showTeacherLogin ? (
          <div className="pass-block" style={{marginBottom:"1.25rem"}}>
            <p className="pass-hint">Teacher sign-in — classes are free for you.</p>
            <label className="form-label">Email
              <input className="form-input" type="email" autoComplete="username"
                value={tl.email} onChange={e => tl.setEmail(e.target.value)} />
            </label>
            <label className="form-label">Password
              <input className="form-input" type="password" autoComplete="current-password"
                value={tl.password} onChange={e => tl.setPassword(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") tl.submit(); }} />
            </label>
            {tl.error && <p className="pass-feedback invalid">{tl.error}</p>}
            <button className="book-btn" disabled={tl.busy} onClick={tl.submit}>
              {tl.busy ? "Signing in…" : "Sign in"}
            </button>
            <p className="stripe-hint">
              <button className="stripe-pass-link" onClick={() => { setShowTeacherLogin(false); tl.setError(""); }}>
                Cancel
              </button>
            </p>
          </div>
        ) : (
          <p className="stripe-hint">
            <button className="stripe-pass-link" onClick={() => setShowTeacherLogin(true)}>
              Teacher? Sign in to book free →
            </button>
          </p>
        )}
        
        <div className="booking-fields" style={{marginBottom:"1.25rem"}}>
          <label className="form-label">Full Name
            <input className="form-input" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Jane Smith" minLength={2} required />
          </label>
          <label className="form-label">Email
            <input className="form-input" type="email" value={email}
              onChange={e => { setEmail(e.target.value); setCodeStatus(null); }}
              onBlur={handleEmailBlur} placeholder="your@email.com" />
          </label>
          <label className="form-label">Phone <span style={{fontWeight:400, opacity:0.6}}>(optional)</span>
          <input className="form-input" type="tel" value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="(555) 123-4567" />
        </label>
        </div>

        {!waiverSigned && email && (
          <p className="em-waiver-note">
            Before your first class, please read and sign our{" "}
            <a href={`/${studio?.studioId}/waiver?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}`}
              target="_blank" rel="noreferrer" className="em-waiver-link">liability waiver</a>.
          </p>
        )}
        {waiverSigned && <p className="em-waiver-signed">✓ Liability waiver on file</p>}

        <div className="pay-mode-tabs pay-mode-tabs--three">
          <button className={`pay-mode-tab ${payMode === "dropin" ? "active" : ""}`}
            onClick={() => { setPayMode("dropin"); setCodeStatus(null); setPassData(null); }}>
            Drop-in
            {payMode === "dropin" && <span className="pay-default-badge">default</span>}
          </button>
          {!isSlidingScale && (
            <button className={`pay-mode-tab ${payMode === "pass" ? "active" : ""}`}
              onClick={() => setPayMode("pass")}>
              Pass / Membership
            </button>
          )}
          {!isSlidingScale && (
            <button type="button" className="pay-mode-tab pay-mode-buy"
              onClick={() => setShowPurchaseModal(true)}>
              Buy a Pass
            </button>
          )}
        </div> 
       {payMode === "dropin" && !showPurchaseModal && (
          <div className="stripe-block">
            {capStatus === 'full' && (
              <div className="em-cap-alert em-cap-alert--full">
                This class is full.
              </div>
            )}
            {capStatus === 'waitlist' && (
              <div className="em-cap-alert em-cap-alert--waitlist">
                This class is full. Join the waitlist and we'll email you if a spot opens while you're first in line.
              </div>
            )}
            {!capStatus && (
              <>
                {isSlidingScale ? (
                  <div className="em-sliding-scale-row" style={{marginBottom:"1rem"}}>
                    <label className="form-label">
                      Choose your price (${event.priceMin}–${event.priceMax})
                      <input
                        className="form-input"
                        type="number"
                        min={event.priceMin}
                        max={event.priceMax}
                        step="1"
                        value={chosenAmount}
                        onChange={e => {
                          const v = Number(e.target.value) || event.priceMin;
                          setChosenAmount(Math.min(event.priceMax, Math.max(event.priceMin, v)));
                        }}
                      />
                    </label>
                  </div>
                ) : (
                  <>
                    <p className="stripe-hint">
                      Have a pass or membership?
                      <button className="stripe-pass-link" onClick={() => setPayMode("pass")}>Use it instead →</button>
                    </p>
                    <div className="em-coupon-row">
                      <input
                        className="em-input"
                        placeholder="Discount code"
                        value={couponCode}
                        onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponDiscount(null); setCouponError(null); }}
                      />
                      <button className="stripe-pass-link" onClick={handleApplyCoupon}>Apply</button>
                    </div>
                    {couponDiscount && (
                      <p className="pass-feedback valid" style={{marginBottom:"0.5rem"}}>
                        ✓ {couponDiscount.displayText} applied
                      </p>
                    )}
                    {couponError && (
                      <p className="pass-feedback invalid" style={{marginBottom:"0.5rem"}}>{couponError}</p>
                    )}
                  </>
                )}
                {stripeReady && (
                  <StripeCardForm
                    key="stripe-card"
                    amount={isSlidingScale
                      ? chosenAmount * 100
                      : couponDiscount
                        ? Math.max(50, Math.round(
                            couponDiscount.type === "percent"
                              ? event.price * 100 * (1 - couponDiscount.amount / 100)
                              : (event.price - couponDiscount.amount) * 100
                          ))
                        : event.price * 100}
                    email={email.trim()}
                    billingDetails={{ email: email.trim() }}
                    onSuccess={handleDropinSuccess}
                    onError={(msg) => setBookError(msg)}
                    submitLabel={isSlidingScale
                      ? `Pay $${chosenAmount} & Book`
                      : couponDiscount
                        ? `Pay $${(couponDiscount.type === "percent"
                            ? event.price * (1 - couponDiscount.amount / 100)
                            : Math.max(0, event.price - couponDiscount.amount)
                          ).toFixed(2)} & Book`
                        : `Pay $${event.price} & Book`}
                    disabled={!nameValid || !emailValid}
                  />
                )}
                {bookError && <p className="pass-feedback invalid" style={{marginTop:"0.75rem"}}>{bookError}</p>}
              </>
            )}
            {capStatus === 'waitlist' && nameValid && emailValid && (
              <button
                className="em-pay-btn"
                onClick={handleJoinWaitlist}
              >
                Join Waitlist
              </button>
            )}
          </div>
        )}
        {payMode === "pass" && (
          <div className="pass-block">
            <p className="pass-hint">Enter your pass code (e.g. YOGA-A3F2) or the email on your membership.</p>
            {passHint && (
              <p className="em-pass-hint">
                ✓ Found your pass — {passHint.classesRemaining} class{passHint.classesRemaining !== 1 ? "es" : ""} remaining
              </p>
            )}
            <div className="pass-input-row">
              <input
                className={`form-input pass-input ${codeStatus === "invalid" ? "input-error" : ""} ${codeStatus === "valid" ? "input-valid" : ""}`}
                value={passCode}
                onChange={e => { setPassCode(e.target.value); setCodeStatus(null); setPassData(null); }}
                placeholder="YOGA-XXXX or email address"
                onKeyDown={e => e.key === "Enter" && handleVerifyCode()}
              />
              <button className="pass-verify-btn"
                disabled={!passCode.trim() || checking || !emailValid}
                onClick={handleVerifyCode}>
                {checking ? "…" : "Check"}
              </button>
            </div>
            {!emailValid && passCode.trim() && (
              <p className="pass-feedback invalid" style={{fontSize:"0.8rem"}}>Enter your email above before checking your pass.</p>
            )}
            {codeStatus === "valid" && (
              <p className="pass-feedback valid">
                ✓ Pass confirmed
                {passData?.classesRemaining != null
                  ? ` · ${passData.classesRemaining} class${passData.classesRemaining !== 1 ? "es" : ""} remaining`
                  : ""}.
              </p>
            )}
            {codeStatus === "invalid" && (
              <div className="pass-feedback invalid">
                <p>That code or email wasn't recognised.</p>
                <button className="pass-buy-link" onClick={() => setShowPurchaseModal(true)}>Buy a pass or membership →</button>
              </div>
            )}
            {bookError && <p className="pass-feedback invalid" style={{marginTop:"0.75rem"}}>{bookError}</p>}
            {capStatus === 'full' && (
            <div className="em-cap-alert em-cap-alert--full" style={{marginBottom:"1rem"}}>
                This class is full.
              </div>
            )}
            {capStatus === 'waitlist' && (
              <div className="em-cap-alert em-cap-alert--waitlist" style={{marginBottom:"1rem"}}>
                This class is full. Join the waitlist and we'll email you if a spot opens while you're first in line.
              </div>
            )}
            {capStatus === 'waitlist' && nameValid && emailValid && (
              <button className="em-pay-btn" onClick={handleJoinWaitlist}>
                Join Waitlist
              </button>
            )}
            <button className="book-btn" style={{marginTop:"1.25rem"}}
              disabled={!canBook || booking || capStatus === 'full' || capStatus === 'waitlist'}
              onClick={handlePassBook}>
              {booking ? "Booking…" : "Confirm Booking"}
            </button>
            {capStatus === 'waitlist' && nameValid && emailValid && (
              <button className="em-pay-btn" style={{marginTop:"0.5rem"}} onClick={handleJoinWaitlist}>
                Join Waitlist
              </button>
            )}
          </div>
        )}

        </>
        )}

        {showPurchaseModal && (
          <PassPurchaseModal
            onClose={() => { setShowPurchaseModal(false); onClose(); }}
            prefillName={name}
            prefillEmail={email}
            onPurchaseComplete={(code) => {
              setShowPurchaseModal(false);
              setPayMode("pass");
              if (code) {
                setPassCode(code);
                handleVerifyCode(code);
              }
              // Recheck waiver status after purchase
              if (email.trim()) {
                fetch(`${API}/waiver/status?email=${encodeURIComponent(email.trim())}`)
                  .then(r => r.json())
                  .then(data => setWaiverSigned(data.waiverSigned ?? false))
                  .catch(err => console.error("EventModal: waiver status recheck failed", err));
              }
            }}
          />
        )}
      </div>
    </div>
  );
}